import React, { useState } from "react"
import { blogGeneratorService, BlogContent, ImageContent } from "../../../services/blogAPI"
import {
  Loader2,
  RefreshCw,
  Send,
  CheckCircle,
  AlertCircle,
  Eye,
  Image as ImageIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import Swal from "sweetalert2"
import { useNavigate } from "react-router-dom"
import { getErrorStatus } from "../../../types/error"

const AutomatedBlog: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [title, setTitle] = useState("")
  const [topic, setTopic] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [generatedData, setGeneratedData] = useState<{
    blogs: BlogContent[]
    images: ImageContent[]
  } | null>(null)
  const [selectedBlogIndex, setSelectedBlogIndex] = useState<number | null>(null)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [previewMode, setPreviewMode] = useState<"blogs" | "images">("blogs")
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null)

  const handleGenerateBlog = async () => {
    if (!title.trim() || !topic.trim()) {
      setError(t("fill_required_fields"))
      return
    }

    setIsGenerating(true)
    setError(null)
    setSuccess(null)
    setGenerationStartTime(Date.now())

    try {
      const data = await blogGeneratorService.generateBlogContent(title, topic)
      setGeneratedData(data)
      setSelectedBlogIndex(0)
      setSelectedImageIndex(0)
      setSuccess(t("blogs_created_successfully"))

      // Toast message
      Swal.fire({
        icon: "success",
        title: t("blogs_created_successfully"),
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      const errorMessage = err instanceof Error ? err.message : t("blog_generation_error")
      setError(errorMessage)

      // Error toast
      Swal.fire({
        icon: "error",
        title: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      })
    } finally {
      setIsGenerating(false)
      setGenerationStartTime(null)
    }
  }

  const handleRegenerateBlog = async () => {
    if (!title.trim() || !topic.trim()) {
      setError(t("fill_required_fields"))
      return
    }

    setIsGenerating(true)
    setError(null)
    setSuccess(null)
    setGenerationStartTime(Date.now())

    try {
      const data = await blogGeneratorService.generateBlogContent(title, topic)
      setGeneratedData(data)
      setSelectedBlogIndex(0)
      setSelectedImageIndex(0)
      setSuccess(t("blogs_regenerated_successfully"))

      // Toast message
      Swal.fire({
        icon: "success",
        title: t("blogs_regenerated_successfully"),
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      const errorMessage = err instanceof Error ? err.message : t("blog_regeneration_error")
      setError(errorMessage)

      // Error toast
      Swal.fire({
        icon: "error",
        title: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      })
    } finally {
      setIsGenerating(false)
      setGenerationStartTime(null)
    }
  }

  const handlePublishToSanity = async () => {
    if (selectedBlogIndex === null || selectedImageIndex === null || !generatedData) {
      setError(t("select_blog_and_image"))
      return
    }

    setIsPublishing(true)
    setError(null)
    setSuccess(null)

    try {
      const selectedBlog = generatedData.blogs[selectedBlogIndex]
      const selectedImage = generatedData.images[selectedImageIndex]

      const result = await blogGeneratorService.publishBlogToSanity(selectedBlog, selectedImage)
      const successMessage = `${t("blog_published_successfully")} Slug: ${result.slug}`
      setSuccess(successMessage)

      // Toast message
      Swal.fire({
        icon: "success",
        title: t("blog_published_successfully"),
        text: `Slug: ${result.slug}`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      })
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        navigate("/login")
      }
      const errorMessage = err instanceof Error ? err.message : t("blog_publishing_error")
      setError(errorMessage)

      // Error toast
      Swal.fire({
        icon: "error",
        title: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const renderBlogContent = (blog: BlogContent) => {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{blog.title}</h2>
        <p className="text-gray-600 dark:text-gray-300 text-sm">{blog.excerpt}</p>
        <div className="prose prose-sm max-w-none dark:prose-invert">
          {blog.body.map((block, index) => {
            switch (block.style) {
              case "h2":
                return (
                  <h2 key={index} className="text-xl font-semibold mt-6 mb-3">
                    {block.children[0]?.text}
                  </h2>
                )
              case "h3":
                return (
                  <h3 key={index} className="text-lg font-semibold mt-4 mb-2">
                    {block.children[0]?.text}
                  </h3>
                )
              case "h4":
                return (
                  <h4 key={index} className="text-base font-semibold mt-3 mb-1">
                    {block.children[0]?.text}
                  </h4>
                )
              case "span":
                return (
                  <p key={index} className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {block.children[0]?.text}
                  </p>
                )
              default:
                return (
                  <p key={index} className="text-gray-700 dark:text-gray-300">
                    {block.children[0]?.text}
                  </p>
                )
            }
          })}
        </div>
      </div>
    )
  }

  const renderImagePrompt = (imagePrompt: any) => {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Görsel Açıklaması</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-gray-700 dark:text-gray-300">Ana Tema</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">{imagePrompt["Core Theme"]}</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 dark:text-gray-300">Ana Konu</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {imagePrompt["Primary Subject"]}
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 dark:text-gray-300">Ortam</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {imagePrompt["Setting & Environment"]}
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 dark:text-gray-300">Aktivite</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">{imagePrompt["Activity"]}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
          {t("automated_blog_generator")}
        </h1>
      </div>

      {/* Input Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        {/* Timeout Info */}
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">{t("blog_creation_info")}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t("blog_creation_info_1")}</li>
                <li>{t("blog_creation_info_2")}</li>
                <li>{t("blog_creation_info_3")}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t("blog_title")}
              </label>
              <textarea
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("blog_title_placeholder")}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none dark:bg-gray-700 dark:text-white resize-none"
              />
            </div>
            <div>
              <label
                htmlFor="topic"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t("blog_theme")}
              </label>
              <textarea
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={t("blog_theme_placeholder")}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none dark:bg-gray-700 dark:text-white resize-none"
              />
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleGenerateBlog}
              disabled={isGenerating || !title.trim() || !topic.trim()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {t("create_blog")}
            </button>

            {isGenerating && generationStartTime && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("creating")} ({t("creating_info")})
              </div>
            )}

            {generatedData && (
              <button
                onClick={handleRegenerateBlog}
                disabled={isGenerating || !title.trim() || !topic.trim()}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {t("regenerate_blog")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">{success}</span>
          </div>
        </div>
      )}

      {/* Generated Content */}
      {generatedData && (
        <div className="space-y-6">
          {/* Preview Mode Toggle */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex space-x-4">
              <button
                onClick={() => setPreviewMode("blogs")}
                className={`flex items-center px-4 py-2 rounded-lg ${
                  previewMode === "blogs"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                <Eye className="w-4 h-4 mr-2" />
                {t("blogs")} ({generatedData.blogs.length})
              </button>
              <button
                onClick={() => setPreviewMode("images")}
                className={`flex items-center px-4 py-2 rounded-lg ${
                  previewMode === "images"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                {t("images")} ({generatedData.images.length})
              </button>
            </div>
          </div>

          {/* Blog Selection */}
          {previewMode === "blogs" && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {t("created_blogs")}
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {generatedData.blogs.map((blog, index) => (
                  <div
                    key={index}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedBlogIndex === index
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                    }`}
                    onClick={() => setSelectedBlogIndex(index)}
                  >
                    <h4 className="font-medium text-gray-800 dark:text-white">{blog.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{blog.excerpt}</p>
                    <div className="mt-2 text-xs text-gray-500">
                      {blog.body.length} {t("paragraphs")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image Selection */}
          {previewMode === "images" && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {t("created_images")}
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {generatedData.images.map((image, index) => (
                  <div
                    key={index}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedImageIndex === index
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                    }`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={image.imageUrl}
                      alt={image.altText}
                      className="w-full h-64 object-cover rounded mb-2"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Content Preview */}
          {selectedBlogIndex !== null && selectedImageIndex !== null && (
            <div className="space-y-6">
              {/* Selected Blog Content */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                  {t("selected_blog")}: {generatedData.blogs[selectedBlogIndex].title}
                </h3>
                {renderBlogContent(generatedData.blogs[selectedBlogIndex])}
              </div>

              {/* Selected Image */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                  {t("selected_image")}
                </h3>
                <div className="flex flex-col md:flex-row gap-6">
                  <img
                    src={generatedData.images[selectedImageIndex].imageUrl}
                    alt={generatedData.images[selectedImageIndex].altText}
                    className="w-full max-w-md mx-auto rounded-lg shadow-md"
                  />
                </div>
              </div>

              {/* Publish Button */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                      {t("publish_to_sanity")}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {t("publish_info")}
                    </p>
                  </div>
                  <button
                    onClick={handlePublishToSanity}
                    disabled={isPublishing}
                    className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPublishing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    {t("publish")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AutomatedBlog
